import React, { useRef } from 'react';
import { Box, Stack } from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';

const PARAGRAPHS = [
  'KBT is not just a synagogue, a beit midrash, or a kehilla. It is all three — and a project to revolutionize the world of Torah in Los Angeles.',
  'Our goal, plainly stated: to share one million hours of Torah-learning across this city over the next decade. Morning seder, evening shiurim, Friday afternoon learning, Daf Yomi, Business Ethics, the Jewish Doctors Network — each is a small contribution to that count.',
  'The doors open for anyone — those who can read every word of the prayerbook, and those who have never opened it. There is a seat for both. Come learn with us.',
];

export default function LetterFromTheRabbi() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const washY = useTransform(scrollYProgress, [0, 1], ['8%', '-8%']);
  const ruleScale = useTransform(scrollYProgress, [0.05, 0.4], [0, 1]);
  const signatureOpacity = useTransform(scrollYProgress, [0.6, 0.85], [0, 1]);
  const signatureY = useTransform(scrollYProgress, [0.6, 0.85], [20, 0]);

  return (
    <Box
      ref={ref}
      component="section"
      sx={{
        position: 'relative',
        py: { xs: 14, md: 24 },
        px: { xs: 3, md: 6 },
        background: '#ffffff',
        overflow: 'hidden',
      }}
    >
      <motion.div
        style={{
          position: 'absolute',
          inset: '-20% 0',
          background:
            'radial-gradient(ellipse at 70% 0%, rgba(15,117,188,0.08), transparent 50%), radial-gradient(ellipse at 10% 100%, rgba(36,34,88,0.08), transparent 55%)',
          y: washY,
          pointerEvents: 'none',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          maxWidth: 1280,
          mx: 'auto',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 2.4fr' },
          gap: { xs: 6, md: 10 },
        }}
      >
        {/* Marginalia column — real portrait of Rabbi Yisraeli */}
        <Box>
          <Box className="kicker" sx={{ mb: 3 }}>From the Rabbi</Box>
          <motion.div
            style={{
              transformOrigin: '0 50%',
              scaleX: ruleScale,
              height: '2px',
              background: 'var(--teal-500)',
              width: '96px',
              marginBottom: '32px',
            }}
          />
          <Box
            sx={{
              fontFamily: 'var(--font-display)',
              fontWeight: 500,
              fontStyle: 'italic',
              fontSize: 'clamp(22px, 2.6vw, 32px)',
              lineHeight: 1.25,
              color: 'var(--navy-500)',
              maxWidth: 340,
            }}
          >
            "A million hours of Torah is not a slogan. It's bookkeeping —
            shiur by shiur, day by day."
          </Box>
          <Box sx={{ mt: 5, display: 'flex', gap: 3, alignItems: 'center' }}>
            <Box
              component="img"
              src="/assets/images/mission.png"
              alt="Rabbi Shlomo Yisraeli"
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid var(--teal-500)',
                flexShrink: 0,
              }}
            />
            <Stack spacing={0.25}>
              <Box
                sx={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 700,
                  fontSize: 18,
                  color: 'var(--navy-500)',
                }}
              >
                Rabbi Shlomo Yisraeli
              </Box>
              <Box
                sx={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10.5,
                  letterSpacing: '0.22em',
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                }}
              >
                Rosh Kehilla, KBT
              </Box>
            </Stack>
          </Box>
        </Box>

        {/* Body essay */}
        <Box>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-15% 0% -15% 0%' }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            style={{
              margin: 0,
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: 'clamp(36px, 5.4vw, 84px)',
              lineHeight: 1.02,
              letterSpacing: '-0.02em',
              color: 'var(--navy-500)',
              marginBottom: 48,
            }}
          >
            One million hours
            <br />
            <span style={{ fontWeight: 500, fontStyle: 'italic', color: 'var(--teal-500)' }}>
              of Torah, by hand.
            </span>
          </motion.h2>

          {PARAGRAPHS.map((p, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-10% 0% -10% 0%' }}
              transition={{ duration: 1, delay: 0.12 * i, ease: [0.22, 1, 0.36, 1] }}
              className={i === 0 ? 'drop-cap' : ''}
              style={{
                margin: '0 0 28px',
                fontFamily: 'var(--font-body)',
                fontWeight: 400,
                fontSize: 'clamp(17px, 1.4vw, 19px)',
                lineHeight: 1.7,
                color: '#1B1A47',
                maxWidth: 680,
              }}
            >
              {p}
            </motion.p>
          ))}

          <motion.div
            style={{
              opacity: signatureOpacity,
              y: signatureY,
              marginTop: 56,
              maxWidth: 680,
            }}
          >
            <Box sx={{ width: '120px', height: '2px', background: 'var(--teal-500)', mb: 4 }} />
            <Box
              sx={{
                fontFamily: 'var(--font-display)',
                fontWeight: 500,
                fontStyle: 'italic',
                fontSize: 36,
                color: 'var(--navy-500)',
                transform: 'skew(-6deg)',
                ml: 1,
              }}
            >
              Shlomo Yisraeli
            </Box>
          </motion.div>
        </Box>
      </Box>
    </Box>
  );
}
